// Read-only Accounts Summary rollup (planning.md §4). Sits directly below the
// Line Items Summary on every screen that shows line items: one row per distinct GL
// account chosen, with that account's summed line total, sorted descending by
// total, plus a grand-total row (which equals the line items total by
// construction — the two blocks read against each other at a glance).
//
// Cross-company splits break down by company: when a line is pinned to a
// company other than the record's, a leading Company column appears, rows are
// keyed by company + account (accounts descending within, companies ordered by
// subtotal), with a per-company subtotal above the grand total — mirroring the
// line items table's own conditional Company column.
//
// Unassigned lines (no account chosen) group under a single "(No account)" row
// so the totals still add up and the gap stays visible.
//
// Derived display only — never editable, never written back. One shared
// component so the surfaces can't drift; callers pass a flat row list and this
// groups/sums.
//
// Presented as a peer section of the Line Items Summary it follows — same heading
// component, same divider, same table-header styling. It used to render its own
// micro-caps heading and column labels, which read as a different design
// language from every block around it (user note 2026-08-02).
import {
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Subtitle2,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
// PORT NOTE: the `compact` heading variant existed for the claim detail modal,
// which titled its sections differently. Claims is gone, so this renders one
// way — as a peer section of the Line Items Summary above it.
import type { ReactNode } from 'react'
import { formatCurrency } from '../lib/format'

/** One line's contribution: which account (+ optional company) and how much. */
export interface AccountsSummaryRow {
  accountId?: string | null
  accountName?: string | null
  companyId?: string | null
  companyName?: string | null
  total: number
}

interface Props {
  rows: AccountsSummaryRow[]
  /** The parent record's company id. A leading Company column + per-company
      breakdown appears only when some row's company differs from it (a genuine
      cross-company split). */
  recordCompanyId?: string | null
}

const NO_ACCOUNT = '(No account)'

const useStyles = makeStyles({
  // Peer section on the invoice surfaces: rule, heading, table — the same
  // rhythm (and the same 16px gaps) as the Line Items Summary above it.
  wrap: { display: 'flex', flexDirection: 'column', gap: '16px' },
  table: { tableLayout: 'fixed', width: '100%', fontVariantNumeric: 'tabular-nums' },
  num: { textAlign: 'right' },
  numHead: {
    textAlign: 'right',
    '& .fui-TableHeaderCell__button': { justifyContent: 'flex-end', width: '100%' },
  },
  none: { color: tokens.colorNeutralForeground3, fontStyle: 'italic' },
  subtotalRow: { color: tokens.colorNeutralForeground2 },
  subtotalLabel: { fontStyle: 'italic' },
  grandRow: { fontWeight: tokens.fontWeightSemibold },
})

function accountLabel(r: AccountsSummaryRow): string {
  const name = r.accountName?.trim()
  if (name) return name
  const id = r.accountId?.trim()
  return id || NO_ACCOUNT
}

/** { key, label, total } grouped by account, sorted descending by total. Keyed
    by account id, else name — a flow-written row can carry a name with no id,
    and must get its own correctly-labelled row (two id-less named accounts stay
    distinct) rather than joining the "(No account)" bucket. Only rows with
    neither id nor name land there. */
function byAccount(rows: AccountsSummaryRow[]): { key: string; label: string; total: number }[] {
  const map = new Map<string, { key: string; label: string; total: number }>()
  for (const r of rows) {
    const key = r.accountId?.trim() || r.accountName?.trim() || NO_ACCOUNT
    const existing = map.get(key)
    if (existing) existing.total += r.total
    else map.set(key, { key, label: accountLabel(r), total: r.total })
  }
  return [...map.values()].sort((a, b) => b.total - a.total)
}

export function AccountsSummary({ rows, recordCompanyId }: Props) {
  const s = useStyles()
  if (!rows || rows.length === 0) return null

  const showCompany = rows.some((r) => r.companyId && r.companyId !== (recordCompanyId ?? undefined))
  const grand = rows.reduce((sum, r) => sum + r.total, 0)

  return (
    <div className={s.wrap}>
      <Divider />
      <Subtitle2>Accounts Summary</Subtitle2>
      <Table size="small" aria-label="Accounts summary" className={s.table}>
        <TableHeader>
          <TableRow>
            {showCompany && <TableHeaderCell>Company</TableHeaderCell>}
            <TableHeaderCell>Account</TableHeaderCell>
            <TableHeaderCell style={{ width: 120 }} className={s.numHead}>Total</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {showCompany ? <CompanyGroups rows={rows} /> : (
            byAccount(rows).map((a) => (
              <TableRow key={a.key}>
                <TableCell className={a.label === NO_ACCOUNT ? s.none : undefined}>{a.label}</TableCell>
                <TableCell className={s.num}>{formatCurrency(a.total)}</TableCell>
              </TableRow>
            ))
          )}
          <TableRow className={s.grandRow}>
            {showCompany && <TableCell />}
            <TableCell>Total</TableCell>
            <TableCell className={s.num}>{formatCurrency(grand)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  )
}

/** Cross-company breakdown: company groups ordered by subtotal (desc), accounts
    descending within each, and a per-company subtotal row above the grand total. */
function CompanyGroups({ rows }: { rows: AccountsSummaryRow[] }) {
  const s = useStyles()
  const map = new Map<string, { label: string; rows: AccountsSummaryRow[]; total: number }>()
  for (const r of rows) {
    const key = r.companyId?.trim() || ''
    const label = r.companyName?.trim() || '(No company)'
    const g = map.get(key)
    if (g) { g.rows.push(r); g.total += r.total }
    else map.set(key, { label, rows: [r], total: r.total })
  }
  const groups = [...map.entries()].sort((a, b) => b[1].total - a[1].total)
  const out: ReactNode[] = []
  for (const [key, g] of groups) {
    const accounts = byAccount(g.rows)
    accounts.forEach((a, i) => {
      out.push(
        <TableRow key={`${key}-${a.key}`}>
          <TableCell>{i === 0 ? g.label : ''}</TableCell>
          <TableCell className={a.label === NO_ACCOUNT ? s.none : undefined}>{a.label}</TableCell>
          <TableCell className={s.num}>{formatCurrency(a.total)}</TableCell>
        </TableRow>,
      )
    })
    out.push(
      <TableRow key={`${key}-subtotal`} className={s.subtotalRow}>
        <TableCell />
        <TableCell className={s.subtotalLabel}>{g.label} subtotal</TableCell>
        <TableCell className={s.num}>{formatCurrency(g.total)}</TableCell>
      </TableRow>,
    )
  }
  return <>{out}</>
}
